import { Service, ServiceFromServer } from "@/code/service";
import { reactive, Ref, ref } from "vue";
import { skapi, ENDPOINTS } from "@/main";
import { user } from "@/code/user";
import Pager from "@/code/pager";

export let serviceIdList: Ref<string[]> = ref([]);
export let serviceList: { [key: string]: Service | Promise<Service> } = reactive({});
export let endOfList = ref(false);
export let didTheFirstPull = false;
export let currentService: Ref<Service> = ref(null);
export let serviceUsers: { [service_id: string]: any } = {};
export let serviceRecords: { [service_id: string]: any } = {};
export let serviceAutoMails: { [service_id: string]: any } = {};
export let serviceBulkMails: { [service_id: string]: any } = {};
export let serviceLoggers: { [service_id: string]: any } = {};
export let serviceClientSecrets: { [service_id: string]: any } = {};
export let aiModelCache: { claude?: any[]; openai?: any[] } = {};
export let aiApiKeyCache: { [service_id: string]: { claude?: string; openai?: string } } = {};
export let aiChatHistoryCache: {
    [key: string]: {
        messages: { role: 'user' | 'assistant'; content: string; isError?: boolean; isPending?: boolean; isPendingOlder?: boolean }[];
        endOfList: boolean;
        startKeyHistory: string[];
    };
} = {};

// In-flight AI agent requests keyed by `${serviceId}#${platform}`.
// Kept at module scope so a pending "..." request survives page navigation:
// if the user leaves the agent view and returns, the component can re-subscribe
// to the same promise and pick up the assistant reply when it resolves.
export let pendingAgentRequests: { [key: string]: Promise<void> } = {};

// Bumped whenever an external surface (e.g. dbfile uploads firing background
// indexing requests) wants the agent chat view to refetch its history. The
// agent view watches the per-service counter and debounces a refetch so the
// pending bubbles for those background requests appear without the user
// having to manually navigate.
export let agentHistoryRefreshTrigger: { [service_id: string]: number } = reactive({});

// Module-scope record of the last `agentHistoryRefreshTrigger[serviceId]`
// value the agent view has already consumed. Persists across mount/unmount
// so a bump that occurred while the agent view was unmounted (e.g. user was
// on the dbfile tab during a batch upload) is detected on next mount and
// forces a fresh history fetch instead of restoring stale cache.
export const agentHistoryRefreshLastSeen: { [service_id: string]: number } = {};

// Bumped when a chatbox (agent.vue) upload batch finishes, so the db file
// manager (dbfile.vue) refreshes its file list to show the newly uploaded
// files. Same lastSeen pattern as above: a bump while dbfile.vue is unmounted
// is detected on its next mount and forces a fresh listing.
export let dbFileRefreshTrigger: { [service_id: string]: number } = reactive({});
export const dbFileRefreshLastSeen: { [service_id: string]: number } = {};

// Bumped when an indexing batch finishes (files uploaded via agent.vue or
// dbfile.vue are indexed into records), so the records view (records.vue)
// refreshes its record list to show the newly indexed records.
export let recordsRefreshTrigger: { [service_id: string]: number } = reactive({});
export const recordsRefreshLastSeen: { [service_id: string]: number } = {};

// ─── Collaborator folder-name → email resolution ──────────────────────────
// A collaborator's uploaded files land in a db-storage folder named by their
// user_id (a uuid). To display those folders by the collaborator's email
// instead of the raw uuid, we resolve uuid → email and cache the pair per
// service. reactive so views (dbfile.vue) re-render when a lazily-fetched email
// arrives. Seeded in bulk by the collaborators page (users.vue) when it fetches
// its user list, and filled lazily (via getUsers) for any uuid not yet known.
export const collaboratorEmailCache: { [service_id: string]: { [user_id: string]: string } } = reactive({});

// In-flight uuid→email lookups keyed `${serviceId}::${userId}`, so concurrent
// folder renders don't fire duplicate getUsers calls for the same collaborator.
const collaboratorEmailInflight: { [key: string]: Promise<string | null> } = {};

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
// A db-storage folder name is a collaborator's user_id only when it's a uuid.
export const isCollaboratorFolderId = (s: any): boolean =>
    typeof s === "string" && UUID_RE.test(s);

// Return the reactive per-service sub-map, creating it on first use. Re-reading
// after assignment yields the reactive PROXY (not the raw object) so later key
// additions trigger re-renders.
const collaboratorEmailMap = (serviceId: string) => {
    if (!collaboratorEmailCache[serviceId]) collaboratorEmailCache[serviceId] = {};
    return collaboratorEmailCache[serviceId];
};

// Bulk-seed the cache from an already-fetched user list (called by the
// collaborators page so folder names resolve instantly, no extra fetch).
export const cacheCollaboratorEmails = (
    serviceId: string,
    users: Array<{ user_id?: string; email?: string }> | null | undefined
) => {
    if (!serviceId || !Array.isArray(users)) return;
    const map = collaboratorEmailMap(serviceId);
    for (const u of users) {
        // The collaborators list is authoritative, so a real (non-blank) email
        // always refreshes the cache — this corrects a "" that a prior lazy
        // lookup cached for a then-emailless collaborator, or a since-changed
        // email. The `u.email` guard still prevents clobbering a known email
        // with a blank; re-assigning an identical value is a reactive no-op.
        if (u?.user_id && typeof u.email === "string" && u.email) {
            map[u.user_id] = u.email;
        }
    }
};

// Resolve a single collaborator user_id → email (cache + in-flight dedup).
// Caches "" for a user with no resolvable email so it isn't refetched forever.
// Returns the email, or null when none is available. Never throws.
export const resolveCollaboratorEmail = async (
    serviceId: string,
    userId: string
): Promise<string | null> => {
    if (!serviceId || !isCollaboratorFolderId(userId)) return null;
    const map = collaboratorEmailMap(serviceId);
    if (map[userId] !== undefined) return map[userId] || null;

    const key = serviceId + "::" + userId;
    if (collaboratorEmailInflight[key]) return collaboratorEmailInflight[key];

    const p = (async (): Promise<string | null> => {
        try {
            const res: any = await skapi.getUsers(
                { service: serviceId, searchFor: "user_id", value: userId, condition: "=" } as any,
                { fetchMore: false }
            );
            const email = res?.list?.[0]?.email || "";
            map[userId] = email; // cache "" too — a no-email user shouldn't refetch
            return email || null;
        } catch (err) {
            // Leave uncached so a later render / refresh can retry.
            return null;
        } finally {
            delete collaboratorEmailInflight[key];
        }
    })();

    collaboratorEmailInflight[key] = p;
    return p;
};

// Table used to privately persist the last AI API key the user entered for
// each service+platform, so we can prefill the input next time without
// requiring a round-trip to the client-secret store.
const AI_API_KEY_TABLE = "ai_api_keys";
const aiApiKeyRecordIdCache: { [unique_id: string]: string } = {};

const aiApiKeyUniqueId = (userId: string, platform: "claude" | "openai") =>
    `${userId}_${platform}`;

// Persist the API key as a private record on the admin user's account.
// Upserts by unique_id; first call creates, subsequent calls update by
// record_id (resolved + cached on demand).
export const saveAiApiKeyPrivately = async (
    serviceId: string,
    platform: "claude" | "openai",
    key: string
) => {
    // console.log("[saveAiApiKeyPrivately] called", { serviceId, platform, hasKey: !!key });
    if (!serviceId || (platform !== "claude" && platform !== "openai") || !key) {
        console.warn("[saveAiApiKeyPrivately] early return: invalid args");
        return;
    }
    const userId = user?.user_id;
    if (!userId) {
        // console.warn("[saveAiApiKeyPrivately] early return: no user_id on user", user);
        return;
    }
    const unique_id = aiApiKeyUniqueId(userId, platform);
    const data = { platform, key };
    try {
        let record_id = aiApiKeyRecordIdCache[unique_id];
        if (!record_id) {
            try {
                const found: any = await skapi.getRecords({ unique_id });
                record_id = found?.list?.[0]?.record_id || "";
                if (record_id) aiApiKeyRecordIdCache[unique_id] = record_id;
            } catch (e) {
                // console.log("[saveAiApiKeyPrivately] getRecords miss", e);
            }
        }
        if (record_id) {
            // console.log("[saveAiApiKeyPrivately] updating existing", record_id);
            await skapi.postRecord(data, { record_id });
        } else {
            // console.log("[saveAiApiKeyPrivately] creating new", { unique_id });
            const created: any = await skapi.postRecord(data, {
                unique_id,
                table: { name: AI_API_KEY_TABLE, access_group: "private" },
            });
            if (created?.record_id) aiApiKeyRecordIdCache[unique_id] = created.record_id;
            // console.log("[saveAiApiKeyPrivately] created", created?.record_id);
        }
    } catch (err) {
        // console.warn("[saveAiApiKeyPrivately] failed", err);
    }
};

// Fetch the last private-record-stored API key for the given platform.
// Returns the raw key string, or "" if none is found / on error.
export const loadAiApiKeyPrivately = async (
    platform: "claude" | "openai"
): Promise<string> => {
    if (platform !== "claude" && platform !== "openai") return "";
    const userId = user?.user_id;
    if (!userId) return "";
    const unique_id = aiApiKeyUniqueId(userId, platform);
    try {
        const found: any = await skapi.getRecords({ unique_id });
        const rec = found?.list?.[0];
        if (rec?.record_id) aiApiKeyRecordIdCache[unique_id] = rec.record_id;
        return rec?.data?.key || "";
    } catch (_) {
        return "";
    }
};

export const projectNameExamples = [
    "My trip to Tokyo",
    "Korean history knowledge",
    "Tax and finance",
    "Restaurant menu and chatbot",
    "Real estate in Toulouse",
    "Travel agency project",
    "My medical records and health tracker",
    "Warehouse inventory management",
];

export const projectDescriptionExamples = [
    "Generate optimized itineraries based on my reservations.",
    "Provide insights and answer questions based on my stored knowledge.",
    "Generate reports from my financial data.",
    "List my restaurant's menu and provide a chatbot for my customers.",
    "Query my real estate listings in Toulouse.",
    "Generate tailored itineraries based on my trip experiences.",
    "Store my medical records and track my health data.",
    "Manage my warehouse inventory, generate restock alerts and reports.",
];

export const pickProjectNamePlaceholder = () =>
    projectNameExamples[Math.floor(Math.random() * projectNameExamples.length)];

export const pickProjectPlaceholders = () => {
    const idx = Math.floor(Math.random() * projectNameExamples.length);
    return {
        name: projectNameExamples[idx],
        description: projectDescriptionExamples[idx] || "",
    };
};

// In-flight AI API-key fetches keyed by `${serviceId}#${platform}`, so a visit-time
// prefetch (main.vue run) and the agent gate (refreshGate) share ONE request
// instead of each firing their own on a direct agent-page load.
export const aiApiKeyInflight: { [key: string]: Promise<string | null> } = {};

// Prefetch + cache a service's AI client-secret (API key) for its configured
// platform. Deduped (returns the cached key, or the shared in-flight request) and
// best-effort (never throws / rejects). Called on project VISIT so the agent gate
// finds the key already warm and skips the listClientSecrets round-trip. Also
// warms it while the user is still on the dashboard, so opening the agent is
// instant.
export function prefetchAiApiKey(service: Service): Promise<string | null> {
    try {
        if (!service) return Promise.resolve(null);
        const raw = service.service?.ai_agent || '';
        const platform = (raw.includes('#') ? raw.split('#')[0] : raw) as 'claude' | 'openai' | string;
        if (platform !== 'claude' && platform !== 'openai') return Promise.resolve(null);
        const serviceId = service.id;
        const cached = aiApiKeyCache[serviceId]?.[platform];
        if (cached) return Promise.resolve(cached);
        const flightKey = serviceId + '#' + platform;
        if (aiApiKeyInflight[flightKey]) return aiApiKeyInflight[flightKey];
        const p = Promise.resolve(service.listClientSecrets(false, platform))
            .then((result: any) => {
                const match = result?.list?.find((s: any) => s.id === platform);
                const key = (match?.key && typeof match.key === 'string' && match.key.trim()) ? match.key : null;
                if (key) {
                    aiApiKeyCache[serviceId] = {
                        ...(aiApiKeyCache[serviceId] || {}),
                        [platform]: key,
                    };
                }
                return key;
            })
            .catch(() => null)
            .finally(() => { delete aiApiKeyInflight[flightKey]; });
        aiApiKeyInflight[flightKey] = p;
        return p;
    } catch {
        return Promise.resolve(null);
    }
}

// NOTE: AI model list is NOT prefetched per-service here anymore. The list is
// identical per platform, and firing it for every project on list load caused a
// burst of duplicate fetches (concurrent same-platform services all missed the
// still-empty aiModelCache). It's now fetched lazily + cached by the agent view
// (loadAvailableModels) when a project's agent page is opened — non-blocking, and
// reused from aiModelCache on subsequent opens.

// ─── Project-entry prefetch: records / users / db dir ────────────────────────
// Fired (fire-and-forget) from the service shell's run() on project entry so
// the Records / Users / Files tabs paint from a warm module cache instead of
// blocking on their first fetch. Each builds the SAME pager + first-page fetch
// the page's own cold path builds (params must stay in sync with
// records.vue/users.vue setUpNewPageList + getPage), and PUBLISHES it only if
// the slot is still empty — if the page mounted mid-prefetch and built its
// own pager, the prefetched one is discarded, never clobbering a live pager.
const recordsPrefetchInflight: { [service_id: string]: Promise<void> } = {};
export function prefetchServiceRecords(service: Service): Promise<void> {
    try {
        const sid = service?.id;
        if (!sid || serviceRecords[sid]) return Promise.resolve();
        if (recordsPrefetchInflight[sid]) return recordsPrefetchInflight[sid];
        const p = (async () => {
            const pager = await Pager.init({
                id: "record_id",
                resultsPerPage: 12,
                sortBy: "record_id",
                order: "desc",
            });
            const fetched: any = await skapi.getRecords(
                { service: sid },
                { fetchMore: false, limit: 120 },
            );
            // Pager cannot hold functions (same strip as records.vue getPage).
            (fetched?.list || []).forEach((r: any) => {
                if (r.bin) {
                    for (let k in r.bin) {
                        r.bin[k] = r.bin[k].map((f: any) => {
                            delete f.getFile;
                            return f;
                        });
                    }
                }
            });
            (pager as any).endOfList = fetched.endOfList;
            if (fetched.list?.length) {
                await pager.insertItems(fetched.list);
            }
            if (!serviceRecords[sid]) {
                serviceRecords[sid] = pager;
                // This fetch is fresh, so any pending refresh bump is satisfied;
                // consuming it keeps records.vue from refetching on mount.
                const cur = recordsRefreshTrigger[sid];
                if (typeof cur === "number") recordsRefreshLastSeen[sid] = cur;
            }
        })().catch(() => { }).finally(() => { delete recordsPrefetchInflight[sid]; });
        recordsPrefetchInflight[sid] = p;
        return p;
    } catch {
        return Promise.resolve();
    }
}

const usersPrefetchInflight: { [service_id: string]: Promise<void> } = {};
export function prefetchServiceUsers(service: Service): Promise<void> {
    try {
        const sid = service?.id;
        if (!sid || serviceUsers[sid]) return Promise.resolve();
        if (usersPrefetchInflight[sid]) return usersPrefetchInflight[sid];
        const p = (async () => {
            const pager = await Pager.init({
                id: "user_id",
                resultsPerPage: 12,
                sortBy: "timestamp",
                order: "desc",
            });
            const fetched: any = await skapi.getUsers(
                {
                    service: sid,
                    searchFor: "timestamp",
                    value: Date.now(),
                    condition: "<=",
                },
                { fetchMore: false, ascending: false, limit: 120 },
            );
            (pager as any).endOfList = fetched.endOfList;
            if (fetched.list?.length) {
                await pager.insertItems(fetched.list);
            }
            if (!serviceUsers[sid]) {
                serviceUsers[sid] = pager;
                // Same side-benefit the users page provides: seed collaborator
                // email labels for dbfile's uuid folders.
                cacheCollaboratorEmails(sid, fetched.list || []);
            }
        })().catch(() => { }).finally(() => { delete usersPrefetchInflight[sid]; });
        usersPrefetchInflight[sid] = p;
        return p;
    } catch {
        return Promise.resolve();
    }
}

// Warms service.dbDirInfo so dbfile.vue's immediate path watcher can start its
// file listing without first waiting a getDirInfo round trip of its own.
export function prefetchDbDirInfo(service: Service): void {
    try {
        void Promise.resolve(service?.getDirInfo?.({ target: "db" })).catch(() => { });
    } catch { /* best-effort */ }
}
// How long after a service lands in the list before its Stripe subscription
// fetch fires. Long enough that a prompt "click into a project" gets its entry
// fetches into the shared request queue first; short enough that the
// cancel/past-due badges still feel immediate on the list.
const STRIPE_SUBS_DEFER_MS = 2500;

export let addServiceToList = (serviceListToAdd: ServiceFromServer[]) => {
    serviceListToAdd.forEach((srvcObj: ServiceFromServer) => {
        let serviceId = srvcObj.service;
        if (!serviceList[serviceId]) {
            serviceList[serviceId] = Service.load(srvcObj).then(s => {
                if (s) {
                    serviceList[serviceId] = s;
                    // Stripe resolves in the BACKGROUND and is now DEFERRED a few
                    // seconds past load: the ctor used to fire one api.stripe.com
                    // request per listed project immediately, and the SDK's shared
                    // request queue drains in whole cycles, so clicking into a
                    // project right then queued its entry fetches behind the
                    // slowest Stripe call. The delay gives entry navigations a
                    // clean queue window; the cancel/past-due badges just settle
                    // a moment later. Derived fields are re-applied THROUGH the
                    // reactive instance (serviceList[serviceId] is the same proxy
                    // Vue uses for currentService) — the raw writes the fetch
                    // makes don't trigger Vue reactivity on their own.
                    setTimeout(() => {
                        const reactiveInst: any = serviceList[serviceId];
                        if (!reactiveInst || typeof reactiveInst.ensureStripeSubscription !== 'function') return;
                        reactiveInst.ensureStripeSubscription().then(() => {
                            if (typeof reactiveInst.applySubscriptionDerived === 'function') {
                                reactiveInst.applySubscriptionDerived();
                            }
                        }).catch(() => { });
                    }, STRIPE_SUBS_DEFER_MS);
                }

                return s;
            });

            if (serviceIdList.value.indexOf(serviceId) === -1) {
                serviceIdList.value.push(serviceId);
            }
        }
    });

    let promisesToWait = Object.values(serviceList).filter(s => s instanceof Promise) as Promise<Service>[];

    return Promise.all(promisesToWait).then(() => {
        // 다 불러오고 난 후에 timestamp 순으로 정렬 체크
        serviceIdList.value.sort((a, b) => {
            let timeA =
                new Date(
                    (serviceList[a] as Service)?.service?.timestamp ||
                    0,
                ).getTime() || 0;
            let timeB =
                new Date(
                    (serviceList[b] as Service)?.service?.timestamp ||
                    0,
                ).getTime() || 0;
            return timeB - timeA;
        });
    });
}

export let pullServices = async (options?: { service_id_to_fetch?: string; service_id_to_exclude?: string[] }) => {
    let {
        admin_private_endpoint
    } = await ENDPOINTS;

    let service_id_to_fetch = options?.service_id_to_fetch || null;

    // console.log("Pulling services with options:", options);
    if (service_id_to_fetch && serviceList[service_id_to_fetch]) {
        // 이미 로드된 서비스인 경우 바로 반환
        return serviceList[service_id_to_fetch];
    }

    let requestOptions: any = {
        service: skapi.service,
        owner: skapi.owner,
        // service_id_to_fetch
        // service_id_to_exclude
    };

    if (service_id_to_fetch) {
        requestOptions.service_id = service_id_to_fetch;
    }

    if (options?.service_id_to_exclude) {
        requestOptions.service_id_to_exclude = options.service_id_to_exclude;
    }

    let fetchOptions: any = {
        limit: 25,
        fetchMore: service_id_to_fetch ? false : didTheFirstPull ? !endOfList.value : false, // 첫 pull 때는 fetchMore 무조건 false
    };

    let srvcs = await skapi.util
        .request(
            admin_private_endpoint + "pull-services",
            requestOptions,
            {
                auth: true,
                fetchOptions,
            }
        )


    if (service_id_to_fetch) {
        if (!srvcs?.list.length) {
            return;
        }
    } else {
        endOfList.value = srvcs?.endOfList;
        didTheFirstPull = true;
    }

    addServiceToList(srvcs.list);

    if (service_id_to_fetch) {
        // wait for the specific service to load
        return serviceList[service_id_to_fetch];
    }
    else {
        return serviceList;
    }
};